import { useCallback, useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CloseOutlined } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { CellContext, RowData } from '@tanstack/react-table';
import useOffers from 'api/useOffers';
import useUsers from 'api/useUsers';
import { Offer } from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import {
  EFeatureFlag,
  OfferType,
  SortingDirection,
  SortingOffersValue
} from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { permissionsUtil } from 'utils/permissionsUtil';
import {
  appearanceUtils,
  statusMapping,
  statusUtils
} from 'utils/scheduleUtils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import AcInput from 'components/AcInput/AcInput';
import ActionButton from 'components/ActionButton/ActionButton';
import Chip from 'components/Chip/Chip';
import ActionCell from 'components/DataTableComponent/components/ActionCell';
import DataTable from 'components/DataTableComponent/DataTable';
import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';
import EmptyState from 'components/EmptyState/EmptyState';
import FilterSearchCard from 'components/Filter/FilterSearchCard';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { EToasterStatus, showToast } from '../../utils/showToast';

const RollingOffersTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { page, rows, sortValue, direction } = usePaginationFromURL('offers');

  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(rows);
  const [sorting, setSorting] = useState<{
    sortValue: SortingOffersValue;
    direction: SortingDirection;
  }>({
    sortValue: sortValue,
    direction: direction
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cheatsOfferId, setCheatsOfferId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState('');
  const [subOffer, setSubOffer] = useState(1);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);

  const { getOffers, deleteOffer, toggleOfferStatus, updateCollectedIndex } =
    useOffers({
      offerType: OfferType.ROLLING_OFFER,
      customPagination: { currentPage, rowsPerPage },
      sorting
    });

  const totalCount = getOffers.data?.totalCount || 0;
  const { fetchFeatureFlags } = useUsers({ publisherId });

  const hasFeatureFlagCheats =
    fetchFeatureFlags.data?.featureFlags?.[
    EFeatureFlag.DASHBOARD_ROLLING_CHEATS
    ];

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rowsPerPage));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rowsPerPage, sorting]);

  const handleTableStateChange = useCallback(() => {
    const dataGridDiv = document.querySelector('.MuiDataGrid-main');
    if (dataGridDiv) {
      dataGridDiv.setAttribute('data-testid', 'dashRollingOfferList');
    }
  }, []);

  const handleCloseCheats = () => {
    setCheatsOfferId(null);
    setPlayerId('');
    setSubOffer(1);
  };

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeleteOffer = () => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Rolling Offer deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete rolling Offer',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      }
    });
  };

  const handleRunCheats = () => {
    updateCollectedIndex.mutate(
      {
        indexCollected: subOffer - 1,
        playerId,
        offerId: cheatsOfferId
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Cheats successfully created',
            status: EToasterStatus.SUCCESS
          });
          handleCloseCheats();
        },
        onError: () => {
          showToast({
            message: 'Error creating cheats',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const toggleRollingStatus = async (offerId: string, active: boolean) => {
    toggleOfferStatus.mutate(
      { active: !active, offerId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Rolling Offer deactivated successfully'
              : 'Rolling Offer activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing rolling offer',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const offers: Offer[] =
    (!getOffers.isLoading &&
      getOffers?.data?.offers &&
      getOffers?.data?.offers.map((offer: Offer) => ({
        _id: offer.publisherOfferId,
        ...offer
      }))) ||
    [];

  const COLUMNS: any = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'offerId',
      header: 'ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'subOffers',
      header: 'Sub Offers',
      cell: ({ row }: any) => row.original.productsSequence?.length || 0
    },

    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<RowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next Appearance',
      cell: ({ row }: any) => {
        const schedule = (row.original as Offer)?.schedule;
        if (schedule?.permanent) return '-';
        return appearanceUtils.getNextUpcomingTime(schedule?.timeFrames) || '-';
      }
    },
    {
      accessorKey: 'actions',
      header: null,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: any) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              ...(hasFeatureFlagCheats
                ? [
                  {
                    label: 'Cheats',
                    handler: () => {
                      setCheatsOfferId(row.original.offerId);
                    },
                    disabled:
                      !permissionsUtil.canUserEdit() ||
                      !permissionsUtil.isActionEnabled(
                        null,
                        EPermissionGroup.OFFERS,
                        EPermissionAction.MANAGE,
                        publisherId
                      )
                  }
                ]
                : []),
              {
                label: 'Edit',
                handler: () => {
                  handleEditOfferUI(row.original.offerId);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  ),
                hidden: !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
              },
              {
                label: 'Edit',
                handler: () => {
                  setSelectedOffer(row.original.offerId);
                  setShowLimitedEditModal(true);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ),
                hidden:
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ) ||
                  permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: row.original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  toggleRollingStatus(
                    row.original.offerId,
                    row.original.active
                  ),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS,
                    publisherId
                  )
              },
              {
                label: 'Duplicate',
                handler: () => handleDuplicateOffer(row.original.offerId),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: 'Delete',
                handler: () => {
                  setIsDeleteDialogOpen(true);
                  setSelectedOffer(row.original.offerId);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              }
            ]}
          />
        </div>
      )
    }
  ];

  const filteredOffers = offers.filter((offer: Offer) => {
    const lowerExternalId = offer.publisherOfferId.toLowerCase();
    const lowerName = offer.name.toLowerCase();
    return (
      lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
      lowerName.includes(nameFilter.toLowerCase())
    );
  });

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const BUTTON_TEXT = `${offers.length > 0 ? 'Add' : 'Create'} New Rolling Offer`;

  const renderContent = () => {
    if (offers.length === 0 && !getOffers.isLoading && !getOffers.isFetching) {
      return (
        <EmptyState
          title="Add your first Rolling Offer"
          description="Rolling offers allow you to create dynamic product sequences that rotate automatically based on your schedule."
          buttonText={BUTTON_TEXT}
          disableButton={!permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredOffers}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashRollingOfferList"
          rowCount={totalCount}
          rowsPerPage={rowsPerPage}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
          isLoading={getOffers.isLoading || getOffers.isFetching}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Rolling Offers"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !permissionsUtil.canUserEdit(),
          hidden: !permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.OFFERS,
            EPermissionAction.MANAGE,
            publisherId
          )
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Rolling Offers External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Rolling Offers Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
        </div>
      )}
      {renderContent()}
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={OfferDesignTypes.RollingOffer}
          onClose={() => setShowLimitedEditModal(false)}
          isOpen={showLimitedEditModal}
          offerType={OfferType.ROLLING_OFFER}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          onOpenChange={(open) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_ROLLING_OFFER_POPUP].title
          }
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_ROLLING_OFFER_POPUP]
              .description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOffer },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
      <Dialog open={!!cheatsOfferId} onClose={handleCloseCheats} fullWidth>
        <Box className="dialog-header" borderBottom="1px solid #bebebe">
          <DialogTitle color="#71717A">Cheats</DialogTitle>
          <CloseOutlined
            sx={{ cursor: 'pointer' }}
            onClick={handleCloseCheats}
          />
        </Box>
        <DialogContent sx={{ mx: 7, my: 2 }}>
          <DialogTitle>Move Player to Sub Offer</DialogTitle>
          <DialogContentText my={1}>
            Enter Player ID and select the destination offer
          </DialogContentText>

          <AcInput
            header="Player ID"
            placeholder="ID number"
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            customClass="my-4"
          />

          <AcInput
            header="Move to sub offer"
            placeholder="Enter sub offer number"
            value={subOffer}
            onChange={(e) => setSubOffer(Number(e.target.value))}
            type="number"
            slotProps={{
              htmlInput: {
                min: 0
              }
            }}
          />
          <DialogActions>
            <ActionButton
              onClick={handleRunCheats}
              text="Run"
              disabled={!playerId}
            />
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RollingOffersTable;
